"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
const faqs = [{
  id: "q01",
  index: "§ Q.01",
  questionStart: "IS MY HOTEL ",
  questionAccent: "READY",
  questionEnd: " FOR REVENUE CONSULTING?",
  answer: "If you are struggling with stagnant occupancy or inconsistent ADR, you are ready. Our data-driven approach identifies hidden revenue leaks and optimizes your OTA presence to ensure your property captures the right demand at the right price point."
}, {
  id: "q02",
  index: "§ Q.02",
  questionStart: "HOW DO YOU IMPROVE ",
  questionAccent: "OTA",
  questionEnd: " PERFORMANCE?",
  answer: "We audit your current listings, optimize content for search visibility, and implement dynamic pricing strategies. By aligning your OTA strategy with your direct booking goals, we maximize your overall RevPAR and market share."
}, {
  id: "q03",
  index: "§ Q.03",
  questionStart: "WHAT ",
  questionAccent: "TECH",
  questionEnd: " STACK DO YOU RECOMMEND?",
  answer: "We evaluate your current PMS, Channel Manager, and Booking Engine. We recommend scalable, integrated solutions that automate manual tasks and provide actionable insights, ensuring your tech stack supports your revenue growth."
}, {
  id: "q04",
  index: "§ Q.04",
  questionStart: "HOW ",
  questionAccent: "FAST",
  questionEnd: " CAN WE SEE RESULTS?",
  answer: "Revenue management is a strategic process. While quick wins in OTA optimization can be seen within weeks, sustainable growth in ADR and RevPAR typically stabilizes within 3 to 6 months of consistent implementation."
}, {
  id: "q05",
  index: "§ Q.05",
  questionStart: "DO YOU PROVIDE ",
  questionAccent: "REVENUE",
  questionEnd: " AUDITS?",
  answer: "Yes. Our comprehensive revenue audits analyze your historical data, competitor pricing, and distribution channels. We provide a clear roadmap to fix inefficiencies and capitalize on untapped market opportunities."
}, {
  id: "q06",
  index: "§ Q.06",
  questionStart: "IS THIS ",
  questionAccent: "STRATEGY",
  questionEnd: " OR AUTOMATION?",
  answer: "It is both. We combine human expertise with advanced hospitality technology. Automation handles the data processing, while our strategic oversight ensures your pricing and distribution decisions align with your hotel's unique brand goals."
}, {
  id: "q07",
  index: "§ Q.07",
  questionStart: "CAN I ",
  questionAccent: "CONSULT",
  questionEnd: " ON A PROJECT BASIS?",
  answer: "Absolutely. Whether you need a one-time revenue audit, a tech implementation project, or ongoing monthly revenue management support, we offer flexible engagement models tailored to your property's specific needs."
}, {
  id: "q08",
  index: "§ Q.08",
  questionStart: "WHERE ARE YOU ",
  questionAccent: "BASED",
  questionEnd: " AND DO YOU TRAVEL?",
  answer: "HotelRevBaBa is led by Sunil Singh. We provide remote consulting globally and are available for on-site property visits and team training sessions. Contact us to discuss your specific location requirements."
}];
export default function RevenueFaq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1290" id="rateparityalertfaq" className="relative bg-slate-50 text-slate-900 py-32 md:py-44 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px origin-left" style={{
        backgroundColor: "#1e3a8a",
        transform: "rotate(-6deg) translateY(0)",
        transformOrigin: "left center"
      }} />
        <div className="absolute top-0 right-0 size-1.5" style={{
        backgroundColor: "#1e3a8a"
      }} />
      </div>

      <div className="relative mx-auto w-full max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="order-1 lg:order-2 lg:col-span-4">
            <div ref={headerRef} className="relative lg:sticky lg:top-28 transition-all duration-[520ms]" style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(24px)"
          }}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-6 select-none font-default leading-none uppercase" style={{
              fontSize: "clamp(12rem, 22vw, 22rem)",
              color: "#1e3a8a",
              opacity: 0.06,
              letterSpacing: "-0.02em",
              zIndex: 0
            }}>
                05
              </div>

              <div className="relative z-10">
                {}
                <div className="flex items-center gap-3 mb-8">
                  <span className="font-sans uppercase text-blue-800/70" style={{
                  fontSize: "clamp(0.75rem, 0.9vw, 0.875rem)",
                  letterSpacing: "0.18em",
                  fontWeight: 500
                }}>
                    § 05 / REVENUE INSIGHTS
                  </span>
                  <span aria-hidden="true" className="block h-px w-10" style={{
                  backgroundColor: "#e2e8f0"
                }} />
                </div>

                {}
                <h2 className="font-default uppercase text-slate-900 mb-6" style={{
                fontSize: "clamp(3rem, 6vw, 5.5rem)",
                lineHeight: 0.86,
                letterSpacing: "-0.015em",
                fontWeight: 700
              }}>
                  YOUR{" "}
                  <span className="font-default italic normal-case" style={{
                  color: "#1e3a8a",
                  fontSize: "0.85em",
                  letterSpacing: "0.01em"
                }}>
                    queries
                  </span>
                  <br />
                  EXPERT ANSWERS
                </h2>

                {}
                <p className="font-default text-slate-700 mb-10 max-w-md" style={{
                fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                lineHeight: 1.65
              }}>
                  Common questions from hotel owners and managers. We provide clear, data-backed insights to help you navigate the complexities of modern revenue management.
                </p>

                {}
                <Button asChild className="group relative bg-blue-700 text-white hover:bg-blue-800 font-default uppercase rounded-lg px-9 py-5 h-auto border-none transition-all duration-[180ms] ease-out hover:shadow-lg w-full sm:w-auto" style={{
                letterSpacing: "0.04em",
                fontSize: "0.9375rem"
              }}>
                  <Link to="/ContactPage">
                    Book A Consultation
                    <ArrowRight className="ml-1 size-4" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="order-2 lg:order-1 lg:col-span-8">
            <div ref={listRef} className="transition-all duration-[520ms]" style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            opacity: listVisible ? 1 : 0,
            transform: listVisible ? "translateY(0)" : "translateY(24px)"
          }}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b-[1.5px] border-slate-200 last:border-b-[1.5px]">
                    <AccordionTrigger className="group py-7 hover:no-underline [&>svg]:hidden rounded-lg gap-6">
                      <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                        <span className="font-mono uppercase text-slate-500 shrink-0 pt-2" style={{
                      fontSize: "0.75rem",
                      letterSpacing: "0.14em",
                      fontWeight: 500
                    }}>
                          {faq.index}
                        </span>
                        <h3 className="font-default uppercase text-slate-900 flex-1" style={{
                      fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
                      lineHeight: 0.95,
                      letterSpacing: "0em",
                      fontWeight: 600
                    }}>
                          {faq.questionStart}
                          <span className="font-default italic normal-case" style={{
                        color: "#1e3a8a",
                        letterSpacing: "0.01em"
                      }}>
                            {faq.questionAccent}
                          </span>
                          {faq.questionEnd}
                        </h3>
                      </div>
                      <span aria-hidden="true" className="shrink-0 inline-flex items-center justify-center size-10 font-default text-3xl leading-none transition-transform duration-[240ms] ease-out group-data-[state=open]:rotate-45" style={{
                    color: "#1e3a8a",
                    fontWeight: 400
                  }}>
                        +
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-0 pb-0">
                      <div className="relative mt-2 mb-4 px-5 md:px-8 py-7 bg-white border-t border-blue-700">
                        <div className="md:pl-[5.5rem]">
                          <p className="font-default text-slate-800 mb-5" style={{
                        fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
                        lineHeight: 1.55,
                        letterSpacing: "-0.005em"
                      }}>
                            {faq.answer}
                          </p>
                          <Link to="/ContactPage" className="group/link inline-flex items-center gap-2 font-default font-semibold" style={{
                        color: "#1e3a8a",
                        fontSize: "0.9375rem"
                      }}>
                            <span className="relative">
                              GET IN TOUCH
                              <span aria-hidden="true" className="absolute left-0 right-0 -bottom-0.5 h-px origin-left scale-x-0 transition-transform duration-[220ms] ease-out group-hover/link:scale-x-100" style={{
                            backgroundColor: "#1e3a8a"
                          }} />
                            </span>
                            <ArrowRight className="size-4 transition-transform duration-[220ms] ease-out group-hover/link:translate-x-1" strokeWidth={1.5} />
                          </Link>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}